import { isJobCancelable } from "src/utils/utility-functions";

export interface DashboardData {
  id: string;
  user: { firstName: string; lastName: string };
  caseNumbers: string[];
  partiesCount: number;
  createdAt: string;
  docsCount: number;
  pagesCount: number;
  trackingNumber: string;
  debtorName: string;
  jointDebtorName?: string;
  orderPreferences?: { sides: string; format: string; service: string };
  mailMethod: string;
  isCancelled: boolean;
  isCertificateRequested: boolean;
  isCertificateDownloadable: boolean;
  isCertificateRequestable: boolean;
  type: string;
  status: 'queue' | 'completed' | 'cancelled' | 'hold' | 'ready' | 'processing';
}

export interface JobDocument {
  createdAt: string;
  id: number;
  jobId: number;
  isChecked: boolean;
  pageCount: number;
  docketText: string;
  fillingDate: string;
  docketDescription: string;
  docketReferenceNumber: string;
  caseDocumentId: string;
  docketFileLink: string;
  type?: string;
  paperColor?: string;
  storedFile: {
    createdAt: string;
    filename: string;
    id: number;
    key: string;
    link: string;
  };
  extarnalFileLink: string | null;
}

export type JobStatusType =
  | 'in_queue'
  | 'completed'
  | 'cancelled'
  | 'on_hold'
  | 'on_hold_manual'
  | 'processing'
  | 'printing'
  | 'completed_payment_declined'
  | 'manual_review'
  | 'draft'
  | 'submitted';

interface JobCaseParty {
  name: string;
  address: string[];
  isCertified: boolean;
  isOvernight: boolean;
  isFirstClass: boolean;
  newJerseyRelationshipOfParty?: string;
}

interface CertificatePreferences {
  customFileName: string;
  shouldAttachDocs: boolean;
  shouldIncludeNEF: boolean;
  hearingInformation: {
    hearingDate: string;
    hearingTime: string;
    responseDate: string;
    hearingLocation: string;
  };
}

export interface JobMailingTypes {
  first_class_domestic: number;
  first_class_canada: number;
  first_class_mexico: number;
  first_class_international: number;
  certified_domestic: number;
  certified_canada: number;
  certified_mexico: number;
  certified_international: number;
  priority_express_domestic: number;
  priority_express_canada: number;
  priority_express_mexico: number;
  priority_express_international: number
  cm_ecf: number;
}

export interface LegacyMailingTypes {
  canada: number;
  canada_priority: number;
  certified: number;
  electronic: number;
  first_class: number;
  first_class_overnight: number;
  first_class_priority: number;
  international: number;
  international_overnight: number;
  international_priority: number;
  mexico: number;
  mexico_priority: number;
  cm_ecf: number;
}

export type PaymentStatusResponse = 'paid' | 'refunded' | 'unpaid' | 'failed' | undefined;
export interface JobResponse {
  createdAt: string;
  id: number;
  caseNumbers: string[];
  trackingNumber: string;
  mailingTypeCounts: JobMailingTypes | null;
  mailingTypes: LegacyMailingTypes | null;
  mailMethod: string;
  isCertificateRequested: boolean;
  isCertificateDownloadable: boolean;
  isCertificateRequestable: boolean;
  courtJurisdiction: string[] | null;
  courtLocation: {
    state: string;
    district: string;
    division: string;
  };
  adversaryProceeding: {
    adversaryDefendant: string;
    adversaryNumber: string;
    adversaryPlaintiff: string;
    estatePartyName?: string;
    isChecked: boolean;
  };
  debtorName: string;
  jointDebtorName?: string;
  isCancelled: boolean;
  type: string;
  clientCategory: string;
  partiesCount: number;
  status: JobStatusType;
  completedAt: string | null;
  documents: JobDocument[];
  httpClient: {
    ip: string;
    host: string;
    userAgent: string;
  };
  orderPreferences: {
    sides: string;
    format: string;
    service: string;
  };
  certificatePreferences: CertificatePreferences;
  returnAddress: {
    city: string;
    state: string;
    zipCode: string;
    firmName: string;
    isDefault: boolean;
    addressOne: string;
    addressTwo: string;
    addressName: string;
  };
  signature: {
    fax: string;
    city: string;
    email: string;
    phone: string;
    state: string;
    zipCode: string;
    firmName: string;
    fullName: string;
    barNumber: string;
    isDefault: boolean;
    addressOne: string;
    addressTwo: string;
    clientRole: string;
  };
  caseParties: JobCaseParty[] | [];
  user: {
    email: string;
    firstName: string;
    lastName: string;
    id: number;
  };
  invoice: InvoiceResponse;
  onHoldAt: string | null;
  processingAt: string | null;
  pagesCount: number;
  sheetsCount: number;
  statusHistory: JobStatusHistory[];
  integrationPartner: string;
  returnEnvelope: {
    address: string;
    city: string;
    name: string;
    office: string;
    state: string;
    zipCode: string;
    isPostageIncluded: boolean;
  } | null;
  dmMailingType: 'direct_mail_personalized' | 'direct_mail_non_personalized' | null;
  organization: { id: number; type: string; name: string } | null;
  order: { id: number; amount: number; paymentStatus: PaymentStatusResponse } | null;
  paymentLink?: string;
  onHoldReason?: 'Payment Declined' | 'Manual' | null;
  printDocuments: [
    {
      id: number;
      recipient: {
        name: string;
        address: string[];
      };
      pbTrackingNumber: string | null;
      storedFile: {
        filename: string;
      };
    }
  ];
  editAuditLogs?: {
    id: number;
    adminFirstName: string;
    adminLastName: string;
    editType: string;
    previousState: {
      documents: {
        filename: string;
        type: string;
        description: string;
        ecfReference: string;
        pageCount: number;
        position: number;
        paperColor?: string;
      }[];
      invoiceTotal: number;
    };
    newState: {
      documents: {
        filename: string;
        type: string;
        description: string;
        ecfReference: string;
        pageCount: number;
        position: number;
        paperColor?: string;
      }[];
      invoiceTotal: number;
    };
    createdAt: string;
  }[] | null;
  // Contact information snapshot (COS-26000) — only present for COS staff/admin
  submitterFirstName?: string | null;
  submitterLastName?: string | null;
  submitterEmail?: string | null;
  organizationPhone?: string | null;
  organizationCountryCode?: string | null;
}

export interface InvoiceResponse {
  total: number;
  salesTax: number;
  subtotal: number;
  postageAndDelivery: number;
  printCredits: number;
  convenienceFee: number;
  documentProcessing: number;
  cmEcfFees: number;
}

export interface JobStatusHistory {
  createdAt: string;
  status: JobStatusType;
}

export const capitalizeWords = (str: string) => {
  return str
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export function getCertificatePreferencesAsString(
  preferences: CertificatePreferences | null | undefined,
): string {
  // Jobs created through the partner API don't carry certificatePreferences, so
  // this can legitimately be null. Object.entries(null) throws and took down the
  // whole job side panel, so bail out early instead.
  if (!preferences) return '';

  const mappings: Record<keyof CertificatePreferences, string> = {
    shouldAttachDocs: 'Documents Attached',
    shouldIncludeNEF: 'NEF Recipients Included',
    customFileName: 'Custom File Name',
    hearingInformation: 'Hearing Information Included',
  };

  const preferencesValues = Object.entries(preferences)
    .filter(([_, value]) => {
      if (typeof value === 'object' && value !== null) {
        return Object.values(value).some((v) => Boolean(v));
      }
      return Boolean(value);
    })
    .map(([key]) => mappings[key as keyof typeof mappings]);

  return ['Included', ...preferencesValues].join(', ');
}

export const transformDate = (createdAt: string): string => {
  const date = new Date(createdAt);

  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const year = date.getFullYear().toString().slice(-2);
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const seconds = date.getSeconds().toString().padStart(2, '0');

  return `${month}/${day}/${year}, ${hours}:${minutes}:${seconds}`;
};

export const getCourtLocation = (
  courtJurisdiction: string[] | null,
  courtLocation: { state: string; division: string; district: string } | null,
): string | null => {
  if (courtJurisdiction) {
    return courtJurisdiction.join(', ');
  }

  if (courtLocation?.district) {
    return `${courtLocation.district} District of ${courtLocation.state} ${courtLocation.division ? `, Divison ${courtLocation.division}` : ''}`;
  } else if (courtLocation?.state) {
    return `${courtLocation?.state} ${courtLocation?.division ? `, Divison ${courtLocation.division}` : ''}`;
  }

  return null;
};

const getJobAction = (
  status: string,
  createdAt: string,
  isCertificateRequested: boolean,
  isCertificateDownloadable: boolean,
  isCertificateRequestable: boolean,
) => {
  if (status === 'in_queue' && isJobCancelable(createdAt)) {
    return 'cancel';
  }

  if (isCertificateRequested) {
    return 'requested';
  }

  if (isCertificateDownloadable) {
    return 'download';
  }

  if (isCertificateRequestable) {
    return 'request';
  }

  return 'disabled';
};

export const formatData = (data: any) => {
  const newData = data.map((el: DashboardData) => {
    const {
      id,
      caseNumbers,
      user,
      partiesCount,
      createdAt,
      docsCount,
      pagesCount,
      trackingNumber,
      debtorName,
      jointDebtorName,
      orderPreferences,
      mailMethod,
      isCancelled,
      isCertificateRequested,
      isCertificateDownloadable,
      isCertificateRequestable,
    } = el;

    // TODO: HAVE TO SAVE THE NUMBER OF PARTIES PER EACH CASE
    // console.log(el);
    return {
      id,
      user: `${user.firstName} ${user.lastName}`,
      tracking: trackingNumber,
      dateTime: transformDate(createdAt),
      debtorName,
      jointDebtorName,
      caseNumbers: caseNumbers?.join(', '),
      docs: docsCount,
      pages: pagesCount,
      parties: partiesCount,
      service: orderPreferences?.service ?? 'normal',
      jobStatus: el.status,
      action: getJobAction(
        el.status,
        el.createdAt,
        isCertificateRequested,
        isCertificateDownloadable,
        isCertificateRequestable,
      ),
      type: el.type.split(' ')[0].charAt(0).toUpperCase() + el.type.split(' ')[0].slice(1).toLowerCase(),
    };
  });

  return newData.sort((a: any, b: any) => new Date(b.dateTime).getTime() - new Date(a.dateTime).getTime());
};

export const getHeaders = (showUserColumn: boolean) => {
  const headers = [
    {
      headerName: 'Job',
      field: 'tracking',
    },
    {
      headerName: 'Date & Time',
      field: 'dateTime',
    },
    {
      headerName: 'Type',
      field: 'type',
    },
    {
      headerName: 'Case Number',
      field: 'caseNumbers',
    },
    {
      headerName: 'Debtor Name',
      field: 'debtorName',
    },
    {
      headerName: 'Docs',
      field: 'docs',
    },
    {
      headerName: 'Pages',
      field: 'pages',
    },
    {
      headerName: 'Parties',
      field: 'parties',
    },
    {
      headerName: 'Service',
      field: 'service',
    },
    { headerName: 'Job Status', field: 'status' },
    {
      headerName: 'Action',
      field: 'action',
      alignSelf: 'center' as 'center'
    },
  ];

  if (showUserColumn) {
    headers.unshift({ headerName: 'User', field: 'user', headerCustomStyles: { width: '6.25rem' } } as any);
  }

  return headers;
};
